import React from 'react';
import { Button } from '@givebutter/primitives/button';
import { faChevronDown, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { show as showIntercomChat } from '@intercom/messenger-js-sdk';
import * as Dialog from 'components/dialog';
import * as DropdownMenu from 'components/dropdown-menu';
import { widgetStrategies } from 'components/pages/campaign-settings/campaign-sharing/widgets/widgetStrategies';
import { Snippet } from 'components/snippet';
import { WidgetInstructions } from 'components/widgets/WidgetInstructions';
import { WIDGETS_GUIDE_LINK } from 'constants/links';
import { WidgetPlatform } from 'constants/widgets';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentWidget } from 'customHooks/useCurrentWidget';

const WidgetEmbedDialog = () => {
    const { data: widget } = useCurrentWidget();
    const { selectedWidgetPlatform, setSelectedWidgetPlatform } = useLocalStorage(state => {
        return {
            selectedWidgetPlatform: state.selectedWidgetPlatform,
            setSelectedWidgetPlatform: state.setSelectedWidgetPlatform,
        };
    });

    const platform = widgetStrategies.find(strat => strat.platform === selectedWidgetPlatform);

    const htmlCode =
        platform?.platform === 'wix'
            ? widget?.display_id
            : `<givebutter-widget id="${widget?.display_id}"></givebutter-widget>`;

    const onClickChatWithSpecialist = (e: React.MouseEvent) => {
        showIntercomChat();
    };

    return (
        <Dialog.Body>
            <div className={'flex w-full flex-col gap-4'}>
                <div className="text-xl font-medium">How to embed</div>
                {platform?.platform !== 'wix' && (
                    <Snippet text={htmlCode} copyProps={{ label: 'Copy' }} inPortal />
                )}
                <div className="bg-gray-25 flex flex-col gap-3 rounded-xl border border-gray-200 p-4">
                    <div className="font-medium">
                        How to embed on{' '}
                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger>
                                <span className="underline">{platform?.name}</span>{' '}
                                <FontAwesomeIcon icon={faChevronDown} />
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content>
                                {widgetStrategies.map(strat => {
                                    return (
                                        <DropdownMenu.Item
                                            key={strat.platform}
                                            onClick={() =>
                                                setSelectedWidgetPlatform(strat.platform)
                                            }
                                        >
                                            {strat.name}
                                        </DropdownMenu.Item>
                                    );
                                })}
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    </div>

                    <hr />

                    <WidgetInstructions
                        inPortal={true}
                        platform={platform?.platform ?? WidgetPlatform.wordpress}
                        steps={platform?.widgetSetup ?? []}
                        widget={widget}
                    />
                    {widget?.type === 'button' && (
                        <div className="flex items-start gap-3 rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-700">
                            <FontAwesomeIcon icon={faInfoCircle} />
                            <div>
                                <div className="font-medium">
                                    I want my button to appear across my entire website!
                                </div>
                                <div>
                                    To ensure your button is visible on every page, embed it into a
                                    consistent element such as your site's header or footer.
                                </div>
                            </div>
                        </div>
                    )}
                    {platform?.platformSetupVideoEmbedSrc !== undefined && (
                        <div className="relative h-0 pb-[62.5%]">
                            <iframe
                                src={platform?.platformSetupVideoEmbedSrc}
                                className="absolute left-0 top-0 h-full w-full"
                                title={`How to embed widgets on ${platform?.name}`}
                                allowFullScreen
                            ></iframe>
                        </div>
                    )}
                </div>
                <div>
                    <div className="font-medium">Still need help?</div>
                    <a
                        target="_blank"
                        rel="noreferrer"
                        href={platform?.helpCenterArticleUrl ?? WIDGETS_GUIDE_LINK}
                    >
                        Read our full guide
                    </a>{' '}
                    or{' '}
                    <Button
                        variant="none"
                        color="primary"
                        className="font-medium"
                        onClick={onClickChatWithSpecialist}
                    >
                        chat with our support team
                    </Button>
                    .
                </div>
            </div>
            <Dialog.Close />
        </Dialog.Body>
    );
};

export { WidgetEmbedDialog };
